import React from 'react';

import { css } from '@emotion/css';
import { RecommendedMinMaxSource } from '@recurrency/core-api-schema/dist/common/enums';
import { Divider } from 'antd';
import { theme } from 'theme';

import { FlexSpace } from 'components/FlexSpace';
import { BorderedPropertyItem } from 'components/recipes/equation/BorderedPropertyItem';
import { EquationPopover } from 'components/recipes/equation/EquationPopover';
import { DemandPattern } from 'components/Tooltip/DemandPatternTooltip';
import { Typography } from 'components/Typography';

import { formatNumber, pluralize } from 'utils/formatting';
import { isMaxShelfLifeEnabled } from 'utils/tenantSettings';

import { EOQFormulaMaxEquation, EOQFormulaMaxEquationData } from './equations/EOQFormulaMaxEquation';
import { EquationSymbol } from './equations/EquationSymbol';
import { RuleOverrideEquation } from './equations/RuleOverrideEquation';

export interface RecommendedMaxData extends EOQFormulaMaxEquationData {
  recommended_max_pre_separation: number;
  recommended_max_pre_shelf_life?: number;
  max_shelf_life_days?: number | null;
  max_shelf_life_stock_cap?: number | null;
  shelf_life_capped?: boolean;
  min_order_quantity?: number;
  min_stock_floor?: number;
  demand_pattern?: string;
  unit_of_measure: string;
  recommended_max_source?: string;
}

export const RecommendedMaxPopover = ({ record }: { record: RecommendedMaxData }) => {
  const maxShelfLifeEnabled = isMaxShelfLifeEnabled();
  const usesMOQ =
    record.min_order_quantity && record.min_order_quantity > 0 && record.demand_pattern !== DemandPattern.NO_DEMAND;
  const showAsNoDemand = !usesMOQ && record.demand_pattern === DemandPattern.NO_DEMAND;
  const showMinEqualMax =
    showAsNoDemand ||
    (record.demand_pattern === DemandPattern.SPARSE &&
      record.recommended_max_source === RecommendedMinMaxSource.Default);
  // Post-separation / pre-shelf-life max. Falls back to final recommended_max when the field is absent.
  const postSeparationMax = record.recommended_max_pre_shelf_life ?? record.recommended_max;
  const maxLessThanOrEqualMin = record.raw_recommended_max <= record.recommended_min && !showAsNoDemand;
  const maxLessThanMinPlusSeparation = record.recommended_max_pre_separation < postSeparationMax && !showAsNoDemand;
  const showShelfLifeCap =
    maxShelfLifeEnabled &&
    !!record.shelf_life_capped &&
    record.max_shelf_life_days != null &&
    record.max_shelf_life_stock_cap != null &&
    !showAsNoDemand;
  const showSecondLine = maxLessThanOrEqualMin || maxLessThanMinPlusSeparation || showShelfLifeCap;
  return (
    <EquationPopover title="Recommended Max Calculation">
      <FlexSpace
        direction="column"
        align-items="start"
        className={css`
          align-content: flex-start;
        `}
      >
        {showMinEqualMax ? (
          <>
            <FlexSpace direction="row">
              <BorderedPropertyItem
                label="Recommended Min"
                value={`${formatNumber(record.recommended_min)} ${record.unit_of_measure}`}
              />
              <EquationSymbol character="=" />
              <BorderedPropertyItem
                label="Recommended Max"
                value={`${formatNumber(record.recommended_min)} ${record.unit_of_measure}`}
              />
            </FlexSpace>
            <div>
              {showAsNoDemand ? (
                <>
                  For items with no demand, recommended min and recommended max
                  <br />
                  will be the same
                </>
              ) : (
                <>
                  Sparse items are set to hold a specified number of months of
                  <br />
                  demand, so recommended min and recommended max will be the same
                </>
              )}
            </div>
          </>
        ) : record.recommended_max_source === RecommendedMinMaxSource.Automated ? (
          <RuleOverrideEquation
            type="Recommended Max"
            value={record.raw_recommended_max}
            uom={record.unit_of_measure}
          />
        ) : (
          <EOQFormulaMaxEquation record={record} />
        )}
        {showSecondLine && (
          <Divider
            className={css`
              padding: 0px;
              margin: 0px;
            `}
          />
        )}
        {maxLessThanOrEqualMin && !maxLessThanMinPlusSeparation && (
          <>
            <FlexSpace direction="row">
              <BorderedPropertyItem
                label="Recommended Max"
                value={`${formatNumber(record.raw_recommended_max)} ${record.unit_of_measure}`}
              />
              <EquationSymbol character="<" />
              <BorderedPropertyItem
                label="Recommended Min"
                value={`${formatNumber(record.recommended_min)} ${record.unit_of_measure}`}
              />
            </FlexSpace>
            <div>
              Recommended max value is less than the recommended min, therefore the recommended min value will be used.
            </div>
          </>
        )}
        {maxLessThanMinPlusSeparation && (
          <>
            <FlexSpace direction="row">
              <BorderedPropertyItem
                label="Recommended Max"
                value={`${formatNumber(record.raw_recommended_max)} ${record.unit_of_measure}`}
              />
              <EquationSymbol character="<" />
              <BorderedPropertyItem
                label="Recommended Min"
                value={`${formatNumber(record.recommended_min)} ${record.unit_of_measure}`}
              />
              <EquationSymbol character="+" />
              <BorderedPropertyItem
                label={usesMOQ ? 'Minimum Order Quantity' : 'EOQ'}
                value={`${usesMOQ ? formatNumber(record.min_order_quantity) : formatNumber(record.eoq)} ${
                  record.unit_of_measure
                }`}
              />
              <EquationSymbol character="=" />
              <BorderedPropertyItem
                label={`Recommended Max with ${usesMOQ ? ' MOQ' : ' EOQ'}`}
                value={`${formatNumber(postSeparationMax)} ${record.unit_of_measure}`}
              />
            </FlexSpace>
            {usesMOQ ? (
              <>
                This item has an MOQ so the max must be greater than the min and MOQ to guarantee the correct order size
                in all recommended orders.
              </>
            ) : (
              <>Per setting, Max adjusted to be one EOQ above the Min.</>
            )}
          </>
        )}
        {showShelfLifeCap && (
          <>
            {(maxLessThanOrEqualMin || maxLessThanMinPlusSeparation) && (
              <Divider
                className={css`
                  padding: 0px;
                  margin: 0px;
                `}
              />
            )}
            <FlexSpace direction="row">
              <BorderedPropertyItem
                label="Recommended Max"
                value={`${formatNumber(postSeparationMax)} ${record.unit_of_measure}`}
              />
              <EquationSymbol character="→" />
              <BorderedPropertyItem
                label="Max Shelf Life Cap"
                value={`${formatNumber(record.max_shelf_life_stock_cap)} ${record.unit_of_measure}`}
              />
              <EquationSymbol character="=" />
              <BorderedPropertyItem
                label="Recommended Max"
                value={`${formatNumber(record.recommended_max)} ${record.unit_of_measure}`}
              />
            </FlexSpace>
            <Typography type="small" style={{ color: theme.colors.primary[500] }}>
              Recommended max capped — max shelf life of{' '}
              {pluralize(record.max_shelf_life_days ?? 0, 'day', 'days', true)} (round(
              {formatNumber(record.max_shelf_life_days)} × {formatNumber(record.avg_demand)}) ={' '}
              {formatNumber(record.max_shelf_life_stock_cap)} {record.unit_of_measure}).
            </Typography>
          </>
        )}
      </FlexSpace>
    </EquationPopover>
  );
};
